import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { cn } from '@/lib/utils';
import { formatError } from '@/lib/errors';
import {
  ADJUSTMENT_REASONS,
  ADJUSTMENT_REASON_LABELS,
  useInventoryLevels,
  useItem,
  useLocations,
  useRecordAdjustment,
  type AdjustmentReason,
  type Item,
  type Location,
} from '@/features/inventory/hooks';

const ADJUSTMENT_MODES = ['cycle_count', 'adjustment'] as const;
type AdjustmentMode = (typeof ADJUSTMENT_MODES)[number];

const MODE_COPY: Record<AdjustmentMode, { label: string }> = {
  cycle_count: {
    label: 'Cycle count',
  },
  adjustment: {
    label: 'Adjustment',
  },
};

interface AdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lock the dialog to a single item. */
  itemId?: string;
  defaultMode?: AdjustmentMode;
  onRecorded?: () => void;
}

/**
 * Single-item count / adjustment entry. The operator picks an item, a location,
 * and (for lot-tracked items) a lot, then enters the counted quantity. We
 * read the current system on-hand from `inventory_levels` and post a
 * single reason-coded adjustment movement for the delta. A positive delta
 * flows in; a negative delta flows out.
 */
export function AdjustmentDialog({
  open,
  onOpenChange,
  itemId,
  defaultMode = 'cycle_count',
  onRecorded,
}: AdjustmentDialogProps) {
  const recordAdjustment = useRecordAdjustment();
  const { data: locations } = useLocations();
  const { data: lockedItem } = useItem(itemId);

  const [mode, setMode] = useState<AdjustmentMode>(defaultMode);
  const [pickedItem, setPickedItem] = useState<Item | null>(null);
  const [locationId, setLocationId] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState<AdjustmentReason>('count_correction');
  const [memo, setMemo] = useState('');

  const item = itemId ? (lockedItem ?? null) : pickedItem;
  const locationRows = useMemo<Location[]>(() => locations ?? [], [locations]);

  // Live on-hand for the chosen item so we can compute + preview the delta.
  const { data: levels } = useInventoryLevels(item?.id);

  useEffect(() => {
    if (!open) {
      setMode(defaultMode);
      setPickedItem(null);
      setLocationId('');
      setLotNumber('');
      setCounted('');
      setReason('count_correction');
      setMemo('');
    }
  }, [open, defaultMode]);

  // Default the reason to the natural one per mode when the operator
  // switches between cycle count and adjustment.
  useEffect(() => {
    setReason(mode === 'cycle_count' ? 'count_correction' : 'damage');
  }, [mode]);

  // Current on-hand at the selected location + lot. Lot-tracked items match
  // on the typed lot number (blank = the no-lot bucket); non-lot items
  // ignore the lot dimension entirely.
  const currentQuantity = useMemo(() => {
    if (!item || !locationId) return 0;
    const trimmedLot = lotNumber.trim();
    const rows = (levels ?? []).filter((l) => l.location_id === locationId);
    const match = rows.filter((l) =>
      item.is_lot_tracked
        ? (l.lots?.lot_number ?? '') === trimmedLot
        : true,
    );
    return match.reduce((acc, l) => acc + (l.quantity ?? 0), 0);
  }, [item, locationId, lotNumber, levels]);

  const countedNum = Number(counted);
  const hasCount = counted.trim() !== '' && Number.isFinite(countedNum) && countedNum >= 0;
  const delta = hasCount ? Math.round(countedNum) - currentQuantity : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) {
      toast.error('Pick an item first.');
      return;
    }
    if (!locationId) {
      toast.error('Pick a location.');
      return;
    }
    if (!hasCount) {
      toast.error('Enter the counted quantity.');
      return;
    }

    try {
      const { delta: applied } = await recordAdjustment.mutateAsync({
        itemId: item.id,
        locationId,
        lotNumber: item.is_lot_tracked ? lotNumber : null,
        countedQuantity: Math.round(countedNum),
        currentQuantity,
        reasonCode: reason,
        memo,
        type: mode,
      });
      toast.success(
        `Adjusted ${item.sku} by ${applied > 0 ? '+' : ''}${applied} ${item.base_uom}`,
      );
      onOpenChange(false);
      onRecorded?.();
    } catch (err) {
      toast.error(formatError(err, 'Could not record adjustment.'));
    }
  };

  const submitting = recordAdjustment.isPending;
  const noLocations = locationRows.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{MODE_COPY[mode].label}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="adjustment-mode">Mode</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as AdjustmentMode)}>
                <SelectTrigger id="adjustment-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_MODES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {MODE_COPY[m].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {itemId ? (
              <div className="space-y-1.5">
                <Label>Item</Label>
                <div className="rounded-md border border-border px-3 py-2 text-sm">
                  {item ? (
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{item.sku}</span>
                      <span className="truncate">{item.name}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Loading…</span>
                  )}
                </div>
              </div>
            ) : (
              <ItemPicker value={pickedItem?.id ?? null} onChange={setPickedItem} autoFocus />
            )}

            {noLocations && (
              <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                No active locations yet.
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="adjustment-location">Location</Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger id="adjustment-location">
                    <SelectValue placeholder="Pick a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locationRows.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {item?.is_lot_tracked && (
                <div className="space-y-1.5">
                  <Label htmlFor="adjustment-lot">Lot number</Label>
                  <Input
                    id="adjustment-lot"
                    value={lotNumber}
                    onChange={(e) => setLotNumber(e.target.value)}
                    placeholder="Leave blank for no lot"
                    maxLength={120}
                    className="font-mono text-xs"
                  />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="adjustment-reason">Reason</Label>
              <Select value={reason} onValueChange={(v) => setReason(v as AdjustmentReason)}>
                <SelectTrigger id="adjustment-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ADJUSTMENT_REASON_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Current on-hand</Label>
                <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm tabular-nums text-muted-foreground">
                  {locationId ? (
                    <>
                      {currentQuantity.toLocaleString()}
                      {item ? ` ${item.base_uom}` : ''}
                    </>
                  ) : (
                    'Pick a location'
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="adjustment-counted">
                  Counted quantity <span className="text-fresh-salmon-dark">*</span>
                </Label>
                <Input
                  id="adjustment-counted"
                  value={counted}
                  onChange={(e) => setCounted(e.target.value)}
                  inputMode="numeric"
                  placeholder="0"
                  className="tabular-nums"
                />
              </div>
            </div>

            {hasCount && locationId && (
              <div
                className={cn(
                  'rounded-md border px-3 py-2 text-sm',
                  delta === 0
                    ? 'border-border bg-secondary/40 text-muted-foreground'
                    : delta > 0
                      ? 'border-bright-moss-dark/40 bg-bright-moss-dark/10 text-foreground'
                      : 'border-fresh-salmon-dark/40 bg-fresh-salmon-dark/10 text-foreground',
                )}
              >
                {delta === 0 ? (
                  'No change — the count matches the current on-hand.'
                ) : (
                  <span className="tabular-nums">
                    Adjustment:{' '}
                    <span className="font-medium">
                      {delta > 0 ? '+' : ''}
                      {delta.toLocaleString()}
                    </span>{' '}
                    {item?.base_uom} ({currentQuantity.toLocaleString()} →{' '}
                    {Math.round(countedNum).toLocaleString()})
                  </span>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="adjustment-memo">Memo</Label>
              <Textarea
                id="adjustment-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Optional note for the audit trail."
                rows={2}
                maxLength={1000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || noLocations || delta === 0}>
              {submitting ? 'Posting…' : 'Post adjustment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
